import { isQualifyingTriggerReason } from 'utils/speciesCriteriaMet/normalizeTriggerReasonId'

const EMPTY_CRITERIA_VALUE = '-'

const isMeaningfulCriteria = (criteria) => {
  if (criteria == null) {
    return false
  }

  const normalized = String(criteria).trim()
  return normalized !== '' && normalized !== EMPTY_CRITERIA_VALUE
}

const splitCriteriaString = (value) => {
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(isMeaningfulCriteria)
}

export const extractKbaCriteriaFromItem = (item) => {
  if (!item) {
    return []
  }

  if (Array.isArray(item.kbaCriterias)) {
    return item.kbaCriterias.filter(isMeaningfulCriteria)
  }

  if (Array.isArray(item.kbaCriteria)) {
    return item.kbaCriteria.filter(isMeaningfulCriteria)
  }

  if (typeof item.kbaCriteria === 'string') {
    return splitCriteriaString(item.kbaCriteria)
  }

  return []
}

/**
 * Derive overall criteria from nested species calculated payload (GET /sites/:id `kbaCriteria`).
 * Walks SpeciesAssesmentParameters because top-level species objects often lack kbaCriterias.
 */
export const extractOverallCriteriaFromSiteKbaCriteria = (kbaCriteria) => {
  if (!Array.isArray(kbaCriteria) || kbaCriteria.length === 0) {
    return []
  }

  const criteria = new Set()

  kbaCriteria.forEach((species) => {
    extractKbaCriteriaFromItem(species).forEach((value) => criteria.add(value))

    const parameters =
      species?.SpeciesAssesmentParameters ||
      species?.speciesAssesmentParametersDisplay ||
      []

    if (!Array.isArray(parameters)) {
      return
    }

    parameters.forEach((param) => {
      extractKbaCriteriaFromItem(param).forEach((value) => criteria.add(value))
    })
  })

  return [...criteria]
}

/**
 * Catalog (non-reassessment) fallback when live calc APIs return empty.
 * Prefer list-style siteCriterias, then nested site.kbaCriteria.
 */
export const resolveCatalogCriteriaFallback = (site) => {
  if (Array.isArray(site?.siteCriterias) && site.siteCriterias.length > 0) {
    return site.siteCriterias.filter(isMeaningfulCriteria)
  }

  return extractOverallCriteriaFromSiteKbaCriteria(site?.kbaCriteria)
}

const shouldIncludeTriggerRow = (item, isReassessment) => {
  if (!isReassessment) {
    return true
  }

  if (item?.triggerReasonId == null) {
    return true
  }

  return isQualifyingTriggerReason(item.triggerReasonId)
}

const collectFromList = (list, isReassessment) => {
  if (!Array.isArray(list)) {
    return []
  }

  return list
    .filter((item) => shouldIncludeTriggerRow(item, isReassessment))
    .flatMap(extractKbaCriteriaFromItem)
}

export const collectTreatmentKbaCriteria = (treatment = {}) => {
  const criteria = []
  const { ecologicalIntegrity, irreplaceability } = treatment || {}

  if (Array.isArray(ecologicalIntegrity) && ecologicalIntegrity.some((item) => item?.ecologicalIntegrity === true)) {
    criteria.push('C')
  }

  if (Array.isArray(irreplaceability) && irreplaceability.some((item) => item?.irreplaceability === true)) {
    criteria.push('E')
  }

  return criteria
}

/**
 * @param {object} sources
 * @param {Array} [sources.species]
 * @param {Array} [sources.eco]
 * @param {object} [sources.treatment]
 * @param {boolean} [sources.isReassessment]
 * @returns {string[]} unique KBA criteria met across all triggers (unsorted)
 */
export const computeOverallKbaCriteria = ({
  species = [],
  eco = [],
  treatment = {},
  isReassessment = false
} = {}) => {
  const all = [
    ...collectFromList(species, isReassessment),
    ...collectFromList(eco, isReassessment),
    ...collectTreatmentKbaCriteria(treatment)
  ]

  return [...new Set(all)]
}
